import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CalloutComponent, CalloutPlacement } from '@freelancer/ui/callout';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureDisplay } from '@freelancer/ui/picture';
import { FontType, TextSize } from '@freelancer/ui/text';
import { LinkColor, LinkHoverColor, LinkUnderline } from '../link';
import {
  DropdownFilterContentSize,
  DropdownFilterIconColor,
  DropdownFilterSize,
} from './dropdown-filter.types';

@Component({
  selector: 'fl-dropdown-filter',
  template: `
    <fl-callout
      [edgeToEdge]="true"
      [hideArrow]="true"
      [hideCloseButton]="true"
      [placement]="contentPlacement"
      [mobileCloseButton]="false"
      [useSlideAnimation]="true"
      (calloutOpen)="toggleState()"
      (calloutClose)="toggleState()"
    >
      <fl-callout-trigger
        class="DropdownFilterTrigger"
        [attr.disabled]="disabled"
        (trigger)="handleTrigger()"
      >
        <fl-button
          *ngIf="!iconOnly && !isTextButton"
          class="DropdownFilterButton"
          #button
          [ngClass]="{ IsActive: isActive, BorderRadius: borderRadius }"
          [color]="ButtonColor.CUSTOM"
          [display]="'block'"
          [size]="size"
          [disabled]="disabled"
          [attr.data-applied]="filterApplied"
        >
          <ng-container [ngTemplateOutlet]="dropdownContent"></ng-container>
        </fl-button>
        <fl-link
          *ngIf="!iconOnly && isTextButton"
          #button
          [ngClass]="{ IsActive: isActive }"
          [color]="LinkColor.INHERIT"
          [disabled]="disabled"
          [hoverColor]="LinkHoverColor.INHERIT"
          [underline]="LinkUnderline.NEVER"
          [size]="linkSize"
          [attr.data-applied]="filterApplied"
        >
          <ng-container [ngTemplateOutlet]="dropdownContent"></ng-container>
        </fl-link>
        <ng-template #dropdownContent>
          <div [class.DropdownFilterLabel]="!hideDropdownIcon">
            <div class="DropdownFilterButtonInner">
              <fl-icon
                *ngIf="iconName"
                class="DropdownFilterIcon"
                [label]="iconLabel"
                [name]="iconName"
                [size]="IconSize.SMALL"
                [flMarginRight]="'xxsmall'"
              ></fl-icon>
              <div
                *ngIf="logoSrc && !iconName"
                class="DropdownLogo"
                [flMarginRight]="'xxsmall'"
              >
                <fl-picture
                  [src]="logoSrc"
                  [alt]="logoAlt"
                  [fullWidth]="true"
                  [display]="PictureDisplay.BLOCK"
                ></fl-picture>
              </div>
              <div class="DropdownFilterButtonText">
                <ng-container
                  *ngIf="
                    filterApplied && buttonTextApplied;
                    else plainButtonText
                  "
                >
                  {{ buttonTextApplied }}
                </ng-container>
                <ng-template #plainButtonText>
                  {{ buttonText }}
                </ng-template>
              </div>
            </div>
            <fl-icon
              *ngIf="!hideDropdownIcon"
              class="DropdownFilterArrow"
              [name]="'ui-chevron-down'"
              [size]="IconSize.SMALL"
            ></fl-icon>
          </div>
        </ng-template>
        <ng-container *ngIf="iconOnly">
          <fl-icon
            *ngIf="iconName"
            [clickable]="true"
            [color]="iconColor"
            [label]="iconLabel"
            [name]="iconName"
            [size]="iconSize"
          ></fl-icon>
          <div
            *ngIf="unreadIndicator"
            class="UnreadPosition"
          >
            <fl-unread-indicator
              label="Unread Indicator"
              i18n-label="Unread indicator label"
            ></fl-unread-indicator>
          </div>
        </ng-container>
      </fl-callout-trigger>

      <fl-callout-content>
        <fl-text
          class="DropdownFilterContent"
          #content
          [fontType]="FontType.CONTAINER"
          [attr.data-edge-to-edge]="edgeToEdge"
          (click)="handleContentClicked()"
        >
          <ng-content></ng-content>
        </fl-text>
      </fl-callout-content>
    </fl-callout>
  `,
  styleUrls: ['./dropdown-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownFilterComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CalloutPlacement = CalloutPlacement;
  IconColor = IconColor;
  IconSize = IconSize;
  FontType = FontType;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkUnderline = LinkUnderline;
  PictureDisplay = PictureDisplay;

  isActive = false;

  @Input() buttonText: string;
  /** Text to display when the filter is applied. Uses the default text if this is undefined */
  @Input() buttonTextApplied?: string;
  @Input() contentPlacement = CalloutPlacement.BOTTOM;
  @Input() contentSize = DropdownFilterContentSize.FLUID;
  @Input() size: DropdownFilterSize = ButtonSize.SMALL;
  @Input() edgeToEdge = false;
  @Input() hideDropdownIcon = false;
  @Input() iconName?: string;
  @Input() iconLabel?: string;
  @Input() logoSrc?: string;
  @Input() logoAlt = '';
  @Input() unreadIndicator = false;
  @Input() linkSize: TextSize = TextSize.XSMALL;

  @HostBinding('attr.disabled')
  @Input()
  disabled = false;

  @HostBinding('attr.data-display')
  @Input()
  display: 'block' | 'inline' = 'inline';

  @Input() filterApplied = false;

  @Input() borderRadius = false;

  /** Automatically close when a value is selected */
  @Input() autoClose = false;

  @Input() iconOnly = false;
  @Input() isTextButton = false;

  /** This only works with iconOnly, and only supports IconColor.FOREGROUND and IconColor.LIGHT. It changes the color of the icon */
  @Input() iconColor: DropdownFilterIconColor = IconColor.FOREGROUND;

  /** This only works with iconOnly. It changes the size of the icon */
  @Input() iconSize = IconSize.MID;

  @ViewChild('button', { read: ElementRef })
  button: ElementRef<HTMLDivElement>;

  @ViewChild('content', { read: ElementRef })
  content: ElementRef<HTMLDivElement>;

  @ViewChild(CalloutComponent)
  calloutComponent: CalloutComponent;

  // FIXME: T300116 Enabling hydration errors on the webapp server
  // TypeError: Cannot read properties of null (reading 'parentElement')
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  constructor(private renderer: Renderer2) {}

  close(): void {
    this.calloutComponent.close();
  }

  handleTrigger(): void {
    // Update callout width when we're about to display it
    // so we'll always get the updated trigger width
    if (this.contentSize === DropdownFilterContentSize.INHERIT) {
      this.setContentWidth();
    }
  }

  toggleState(): void {
    this.isActive = !this.isActive;
  }

  handleContentClicked(): void {
    if (this.autoClose) {
      this.close();
    }
  }

  /**
   * Sets callout content's width with the trigger/button's width
   */
  private setContentWidth(): void {
    const buttonWidth = this.button.nativeElement.getBoundingClientRect().width;

    this.renderer.setStyle(
      this.content.nativeElement,
      'width',
      `${buttonWidth}px`,
    );
  }
}
